import React, { useEffect, useRef, useState } from 'react';
import JSONEditor from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';

// MUI imports
import { Box, Button, Typography, Paper, Grid } from '@mui/material';
import PropTypes from 'prop-types';

const JsonDiffEditor = ({ currentData, fileName, onHandleSave }) => {
  const leftEditorRef = useRef(null);
  const rightEditorRef = useRef(null);
  const leftContainer = useRef(null);
  const rightContainer = useRef(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [oldData, setOldData] = useState(currentData);
  const [latestData, setLatestData] = useState(currentData);

  useEffect(() => {
    if (isEditMode && leftContainer.current && rightContainer.current) {
      // destroy existing editors if any
      if (leftEditorRef.current) leftEditorRef.current.destroy();
      if (rightEditorRef.current) rightEditorRef.current.destroy();

      leftEditorRef.current = new JSONEditor(leftContainer.current, {
        mode: 'code',
        mainMenuBar: false,
        navigationBar: false,
        statusBar: false,
        disabled: true,
      });

      rightEditorRef.current = new JSONEditor(rightContainer.current, {
        mode: 'code',
        mainMenuBar: false,
        navigationBar: true,
        statusBar: true,
        onChangeText: () => {},
      });

      leftEditorRef.current.set(oldData);
      rightEditorRef.current.set(latestData);

      // highlight diffs
    }
  }, [isEditMode, currentData]);

  useEffect(() => {
    setOldData(currentData);
    setLatestData(currentData);
  }, [currentData]);

  const handleEdit = () => {
    setIsEditMode(true);
    if (leftContainer.current && rightContainer.current) {
      leftEditorRef.current = new JSONEditor(leftContainer.current, {
        mode: 'code',
        mainMenuBar: true, // Hide top menu
        navigationBar: false,
        statusBar: false,
        onChangeText: () => {},
        disabled: true, // LEFT = read-only
      });

      rightEditorRef.current = new JSONEditor(rightContainer.current, {
        mode: 'code',
        mainMenuBar: true,
        navigationBar: true,
        statusBar: true,
        onChangeText: () => {},
      });
      leftEditorRef.current.set(oldData);
      rightEditorRef.current.set(latestData);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  const handleSave = () => {
    try {
      const updated = rightEditorRef.current.get();
      setOldData(latestData);
      setLatestData(updated);
      const file = new File([JSON.stringify(updated)], fileName, {
        type: 'application/json',
      });
      const payload = {
        file,
        fileName,
      };
      onHandleSave(payload, updated);
      setIsEditMode(false);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message);
    }
  };

  return (
    <Box p={3}>
      {!isEditMode && (
        <Button
          onClick={handleEdit}
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
        >
          Edit with Diff
        </Button>
      )}

      {isEditMode && (
        <>
          <Button
            onClick={handleSave}
            variant="contained"
            color="success"
            disabled={!isEditMode}
            sx={{ mt: 2 }}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="error"
            disabled={!isEditMode}
            sx={{ mt: 2, marginLeft: 2 }}
          >
            Cancel
          </Button>
        </>
      )}

      {!isEditMode ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              bgcolor: '#f5f5f5',
              p: 2,
              borderRadius: 2,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            <pre>{JSON.stringify(latestData, null, 2)}</pre>
          </Paper>
        </Paper>
      ) : (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Old Data
              </Typography>

              <Paper
                variant="outlined"
                sx={{ height: 400, borderRadius: 2, overflow: 'hidden' }}
                ref={leftContainer}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Editable Latest (Diff Highlighted)
              </Typography>

              <Paper
                variant="outlined"
                sx={{ height: 400, borderRadius: 2, overflow: 'hidden' }}
                ref={rightContainer}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

JsonDiffEditor.propTypes = {
  currentData: PropTypes.object.isRequired,
  fileName: PropTypes.string.isRequired,
  onHandleSave: PropTypes.func.isRequired,
};

export default JsonDiffEditor;
